import React, { useState } from "react";
import { styled } from "@material-ui/styles";
import { IStyledArguments } from "../../types/styled-arguments";
import {
  Box,
  Card,
  Grid,
  Button,
  TextField,
  Typography as MuiTypography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  FormControl,
  InputLabel,
  OutlinedInput,
} from "@material-ui/core";
import { useMutation } from "@apollo/client";
import { ICoachUser } from "../../types";
import {
  CREATE_COACH,
  UPDATE_COACH,
  DELETE_COACH,
} from "./WorkspaceManagementQueries";
import UserAvatar from "../user-avatar";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import SearchIcon from "@material-ui/icons/Search";
import SportsIcon from "@material-ui/icons/Sports";
import ShareIcon from "@material-ui/icons/Share";
import { Formik, Form } from "formik";
import InviteCoachModal from "./InviteCoachModal";

// Styled Components
const CoachCard = styled(Card)(({ theme }: IStyledArguments) => ({
  borderRadius: "16px",
  marginBottom: theme.spacing(2),
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
  transition: "transform 0.2s ease, box-shadow 0.2s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.12)",
  },
}));

const CoachHeader = styled(Box)(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  borderBottom: "1px solid #f0f0f0",
}));

const EmptyState = styled(Box)(({ theme }: IStyledArguments) => ({
  textAlign: "center",
  padding: theme.spacing(6),
  color: theme.palette.text.secondary,
}));

const SearchField = styled(TextField)(({ theme }: IStyledArguments) => ({
  marginBottom: theme.spacing(3),
}));

const StyledFormControl = styled(FormControl)(({ theme }: IStyledArguments) => ({
  marginBottom: theme.spacing(2),
}));

const ActionButton = styled(Button)(({ theme }: IStyledArguments) => ({
  marginLeft: theme.spacing(1),
}));

interface ICoachesManagement {
  coaches: ICoachUser[];
  workspaceId: string;
  onRefresh: () => void;
}

// Simple validation function
const validateCoachForm = (values: any) => {
  const errors: any = {};
  
  if (!values.name) {
    errors.name = "Name is required";
  }
  
  if (!values.surname) {
    errors.surname = "Surname is required";
  }
  
  if (!values.email) {
    errors.email = "Email is required";
  } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)) {
    errors.email = "Invalid email address";
  }
  
  return errors;
};

const CoachesManagement: React.FC<ICoachesManagement> = ({
  coaches,
  workspaceId,
  onRefresh,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [selectedCoach, setSelectedCoach] = useState<ICoachUser | null>(null);

  const [createCoach, { loading: createLoading }] = useMutation(CREATE_COACH, {
    onCompleted: () => {
      onRefresh();
      setAddDialogOpen(false);
    },
    onError: (error) => {
      alert(`Error creating coach: ${error.message}`);
    },
  });

  const [updateCoach, { loading: updateLoading }] = useMutation(UPDATE_COACH, {
    onCompleted: () => {
      onRefresh();
      setEditDialogOpen(false);
      setSelectedCoach(null);
    },
    onError: (error) => {
      alert(`Error updating coach: ${error.message}`);
    },
  });

  const [deleteCoach, { loading: deleteLoading }] = useMutation(DELETE_COACH, {
    onCompleted: () => {
      onRefresh();
      setDeleteConfirmOpen(false);
      setSelectedCoach(null);
    },
    onError: (error) => {
      alert(`Error deleting coach: ${error.message}`);
    },
  });

  const filteredCoaches = coaches.filter((coach) => {
    const fullName = `${coach.name} ${coach.surname}`.toLowerCase();
    const search = searchTerm.toLowerCase();
    return fullName.includes(search);
  });

  const handleOpenAddDialog = () => {
    setAddDialogOpen(true);
  };

  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
  };

  const handleOpenEditDialog = (coach: ICoachUser) => {
    setSelectedCoach(coach);
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedCoach(null);
  };

  const handleOpenDeleteConfirm = (coach: ICoachUser) => {
    setSelectedCoach(coach);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedCoach) {
      deleteCoach({
        variables: {
          id: selectedCoach.id,
        },
      });
    }
  };

  const handleCreateCoach = (values: any) => {
    createCoach({
      variables: {
        ...values,
        workspaceId,
      },
    });
  };

  const handleUpdateCoach = (values: any) => {
    if (selectedCoach) {
      updateCoach({
        variables: {
          id: selectedCoach.id,
          ...values,
        },
      });
    }
  };

  const handleOpenInviteCoachSelect = () => {
    setInviteModalOpen(true);
  };

  const handleCloseInviteCoachSelect = () => {
    setInviteModalOpen(false);
  };

  return (
    <Box p={3}>
      {/* Search Bar and Add Button */}
      <Box display="flex" alignItems="flex-start" mb={3} style={{ gap: 16 }}>
        <SearchField
          fullWidth
          variant="outlined"
          placeholder="Search coaches by name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenAddDialog}
          style={{ minWidth: 150, height: 56, whiteSpace: 'nowrap' }}
        >
          Add Coach
        </Button>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<ShareIcon />}
          onClick={handleOpenInviteCoachSelect}
          style={{ minWidth: 150, height: 56, whiteSpace: 'nowrap' }}
        >
          Invite Coach
        </Button>
      </Box>

      {/* Coaches List */}
      {filteredCoaches.length === 0 ? (
        <EmptyState>
          <SportsIcon style={{ fontSize: 64, color: "#ccc" }} />
          <MuiTypography variant="h6" style={{ marginTop: 16 }}>
            {searchTerm ? "No coaches found" : "No coaches available"}
          </MuiTypography>
          <MuiTypography variant="body2" color="textSecondary">
            {searchTerm
              ? "Try adjusting your search criteria"
              : "Click 'Add Coach' to create a new coach account"}
          </MuiTypography>
        </EmptyState>
      ) : (
        <Grid container spacing={3}>
          {filteredCoaches.map((coach) => (
            <Grid item xs={12} md={6} key={coach.id}>
              <CoachCard>
                <CoachHeader>
                  <UserAvatar
                    user={{
                      name: coach.name,
                      surname: coach.surname,
                    }}
                    style={{ width: 56, height: 56 }}
                  />
                  <Box flex={1}>
                    <MuiTypography variant="h6" style={{ margin: 0 }}>
                      {coach.name} {coach.surname}
                    </MuiTypography>
                  </Box>
                  <ActionButton
                    size="small"
                    color="primary"
                    onClick={() => handleOpenEditDialog(coach)}
                  >
                    <EditIcon fontSize="small" />
                  </ActionButton>
                  <ActionButton
                    size="small"
                    color="secondary"
                    onClick={() => handleOpenDeleteConfirm(coach)}
                  >
                    <DeleteIcon fontSize="small" />
                  </ActionButton>
                </CoachHeader>
              </CoachCard>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Add Coach Dialog */}
      <Dialog open={addDialogOpen} onClose={handleCloseAddDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Coach</DialogTitle>
        <Formik
          initialValues={{
            name: "",
            surname: "",
            email: "",
          }}
          validate={validateCoachForm}
          onSubmit={handleCreateCoach}
        >
          {({ errors, touched, handleChange, handleBlur, values }) => (
            <Form>
              <DialogContent>
                <StyledFormControl fullWidth variant="outlined">
                  <InputLabel>Name *</InputLabel>
                  <OutlinedInput
                    name="name"
                    label="Name *"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.name && Boolean(errors.name)}
                  />
                  {touched.name && errors.name && (
                    <MuiTypography variant="caption" color="error">
                      {errors.name}
                    </MuiTypography>
                  )}
                </StyledFormControl>

                <StyledFormControl fullWidth variant="outlined">
                  <InputLabel>Surname *</InputLabel>
                  <OutlinedInput
                    name="surname"
                    label="Surname *"
                    value={values.surname}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.surname && Boolean(errors.surname)}
                  />
                  {touched.surname && errors.surname && (
                    <MuiTypography variant="caption" color="error">
                      {errors.surname}
                    </MuiTypography>
                  )}
                </StyledFormControl>

                <StyledFormControl fullWidth variant="outlined">
                  <InputLabel>Email *</InputLabel>
                  <OutlinedInput
                    name="email"
                    type="email"
                    label="Email *"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.email && Boolean(errors.email)}
                  />
                  {touched.email && errors.email && (
                    <MuiTypography variant="caption" color="error">
                      {errors.email}
                    </MuiTypography>
                  )}
                </StyledFormControl>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseAddDialog} disabled={createLoading}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  disabled={createLoading}
                >
                  {createLoading ? "Creating..." : "Create Coach"}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>

      {/* Edit Coach Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Coach</DialogTitle>
        {selectedCoach && (
          <Formik
            initialValues={{
              name: selectedCoach.name,
              surname: selectedCoach.surname,
            }}
            validate={validateCoachForm}
            onSubmit={handleUpdateCoach}
          >
            {({ errors, touched, handleChange, handleBlur, values }) => (
              <Form>
                <DialogContent>
                  <StyledFormControl fullWidth variant="outlined">
                    <InputLabel>Name *</InputLabel>
                    <OutlinedInput
                      name="name"
                      label="Name *"
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.name && Boolean(errors.name)}
                    />
                  </StyledFormControl>

                  <StyledFormControl fullWidth variant="outlined">
                    <InputLabel>Surname *</InputLabel>
                    <OutlinedInput
                      name="surname"
                      label="Surname *"
                      value={values.surname}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.surname && Boolean(errors.surname)}
                    />
                  </StyledFormControl>
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleCloseEditDialog} disabled={updateLoading}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    color="primary"
                    variant="contained"
                    disabled={updateLoading}
                  >
                    {updateLoading ? "Updating..." : "Update Coach"}
                  </Button>
                </DialogActions>
              </Form>
            )}
          </Formik>
        )}
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <MuiTypography>
            Are you sure you want to delete coach{" "}
            <strong>
              {selectedCoach?.name} {selectedCoach?.surname}
            </strong>
            ? This action cannot be undone.
          </MuiTypography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="secondary"
            variant="contained"
            disabled={deleteLoading}
          >
            {deleteLoading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invite Coach Modal - Generate Workspace Invitation */}
      <InviteCoachModal
        key={`invite-${inviteModalOpen}`}
        open={inviteModalOpen}
        workspaceId={workspaceId}
        onClose={handleCloseInviteCoachSelect}
      />
    </Box>
  );
};

export default CoachesManagement;
